import React from 'react';
import styled from 'styled-components';
import { Group, Pagination, Text, Menu } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { getTotalPages } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	enablePagination: boolean;
	dataOperations?: MantineTableDataOperations;
	rowCount: number;
}

const StyledPaginationRoot = styled(Pagination.Root)`
	&& [data-active] {
		background-color: ${defaultTheme.v3.colors.blue[0]};
		border: 1px solid ${defaultTheme.v3.colors.blue[6]};
		color: ${defaultTheme.v3.colors.dark[9]};
		font-weight: 400;

		&:hover {
			background-color: ${defaultTheme.v3.colors.blue[0]};
		}
	}
`;

export const MantineTablePagination: React.FC<Props> = ({
	enablePagination,
	dataOperations,
	rowCount,
}) => {
	if (!enablePagination || !dataOperations?.pagination) {
		return null;
	}

	const totalPages = getTotalPages({ rowCount, pageSize: dataOperations.pagination.pageSize });

	// Calculate actual visible rows count
	const startRow = dataOperations.pagination.pageIndex * dataOperations.pagination.pageSize + 1;
	const endRow = Math.min(startRow + dataOperations.pagination.pageSize - 1, rowCount);
	const visibleRowsCount = Math.max(0, endRow - startRow + 1);

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: '16px 8px',
				backgroundColor: defaultTheme.v3.colors.white[0],
			}}
		>
			{/* Left side - Navigation buttons */}
			<StyledPaginationRoot
				total={totalPages}
				value={dataOperations.pagination.pageIndex + 1}
				onChange={(value) => {
					dataOperations.setPagination({
						...dataOperations.pagination,
						pageIndex: value - 1,
					});
				}}
				styles={{
					control: {
						border: 'none',
						backgroundColor: 'transparent',
						color: defaultTheme.v3.colors.dark[9],
						'&:hover': {
							backgroundColor: defaultTheme.v3.colors.gray[1],
						},
					},
				}}
			>
				<Group style={{ gap: '0.5rem' }}>
					<Group
						style={{
							gap: '0.5rem',
							cursor:
								dataOperations.pagination.pageIndex === 0
									? 'not-allowed'
									: 'pointer',
							opacity: dataOperations.pagination.pageIndex === 0 ? 0.5 : 1,
						}}
						onClick={() => {
							if (dataOperations.pagination.pageIndex > 0) {
								dataOperations.setPagination({
									...dataOperations.pagination,
									pageIndex: dataOperations.pagination.pageIndex - 1,
								});
							}
						}}
					>
						<Icon
							variant="arrow-left-v3"
							size="vlg"
							color={defaultTheme.v3.colors.blue[6]}
						/>
						<Text size="sm" c="blue.6" fw={500} style={{ userSelect: 'none' }}>
							Previous
						</Text>
					</Group>
					<Pagination.Items />
					<Group
						style={{
							gap: '0.5rem',
							cursor:
								dataOperations.pagination.pageIndex >= totalPages - 1
									? 'not-allowed'
									: 'pointer',
							opacity:
								dataOperations.pagination.pageIndex >= totalPages - 1 ? 0.5 : 1,
						}}
						onClick={() => {
							if (dataOperations.pagination.pageIndex < totalPages - 1) {
								dataOperations.setPagination({
									...dataOperations.pagination,
									pageIndex: dataOperations.pagination.pageIndex + 1,
								});
							}
						}}
					>
						<Text size="sm" c="blue.6" style={{ userSelect: 'none' }} fw={500}>
							Next
						</Text>
						<Icon
							variant="arrow-right-v3"
							size="vlg"
							color={defaultTheme.v3.colors.blue[6]}
						/>
					</Group>
				</Group>
			</StyledPaginationRoot>

			{/* Right side - Rows per page selector */}
			<Group style={{ gap: '0.5rem', alignItems: 'center' }}>
				<Text size="sm" c="dark.9">
					Showing {visibleRowsCount} rows
				</Text>
				<Menu
					shadow="md"
					width={120}
					styles={{
						item: {
							padding: '0.5rem',
							gap: '0.5rem',
							borderRadius: '0.25rem',
							border: `1px solid ${defaultTheme.v3.colors.white[0]}`,
							'&[data-hovered]': {
								backgroundColor: defaultTheme.v3.colors.blue[0],
								border: `1px solid ${defaultTheme.v3.colors.blue[6]}`,
							},
						},
						dropdown: {
							zIndex: Z_INDEX.MENU,
						},
					}}
				>
					<Menu.Target>
						<Group
							style={{ gap: '0.5rem', cursor: 'pointer' }}
							data-testid={TEST_ELEMENT_IDS.CAP_TABLE_PAGINATION_ROWS_PER_PAGE_TEXT}
						>
							<Icon
								variant="caret-down"
								size="vlg"
								color={defaultTheme.v3.colors.blue[6]}
							/>
							<Text size="sm" c="blue.6" fw={500} data-testid="">
								{dataOperations.pagination.pageSize} per page
							</Text>
						</Group>
					</Menu.Target>
					<Menu.Dropdown>
						{[5, 10, 15, 25, 30, 50, 100].map((pageSize) => (
							<Menu.Item
								key={pageSize}
								onClick={() => {
									dataOperations.setPagination({
										...dataOperations.pagination,
										pageIndex: 0,
										pageSize,
									});
								}}
							>
								<Text size="B6Medium">{pageSize} per page</Text>
							</Menu.Item>
						))}
					</Menu.Dropdown>
				</Menu>
			</Group>
		</div>
	);
};
